import type {
  NotificationDetailDto,
  NotificationItemDto,
  NotificationUnreadDto,
} from '~/types/api/notice'
import { isNoticePageCode, type NoticeDetail, type NoticeItem, type NoticePageCode } from '../types'

type NoticeSource = NotificationItemDto | NotificationDetailDto

const CATEGORY_ICON: Record<NoticeItem['cat'], string> = {
  fund: '💰',
  promo: '🎁',
  system: '📢',
}

/** 仅 system 公告按 message_type 映射；安全提醒用盾，登录提醒用锁 */
const MESSAGE_TYPE_ICON: Record<string, string> = {
  system_notice: '📢',
  security_reminder: '🛡️',
  login_reminder: '🔒',
  maintenance: '📢',
}

/** 新设备登录等安全提醒（s_ 号段 / 跳安全中心 / 登录设备模板） */
const DEVICE_SECURITY_KEY = /new[._-]?device|device[._-]?login|login[._-]?new/

/** 防钓鱼等安全警示，与新设备登录的锁图标区分 */
const PHISH_KEY = /phish|fraud|scam/

const GENERIC_SECURITY_KEY = /(^|[._-])security([._-]|$)/

/** 活动：实时返水用闪电，VIP 俸禄用皇冠，任务和其他活动仍用礼物 */
const REBATE_KEY = /(^|[._-])(rebate|rakeback)([._-]|$)/
const VIP_KEY = /(^|[._-])vip([._-]|$)/

const EMPTY_UNREAD: NotificationUnreadDto = {
  all: 0,
  fund: 0,
  promo: 0,
  system: 0,
}

export function emptyNoticeUnread(): NotificationUnreadDto {
  return { ...EMPTY_UNREAD }
}

function mapPage(item: NoticeSource): NoticePageCode | undefined {
  if (item.cta_type !== 'route' || !item.cta_target) return undefined
  const target = item.cta_target.trim().toLowerCase()
  const page = target === 'vip' ? 'rewards_vip' : target
  return isNoticePageCode(page) ? page : undefined
}

/** 活动消息按跳转页或模板区分返水、VIP；对不上时仍用分类默认礼物 */
function promoIcon(target: string, key: string): string | undefined {
  if (target === 'rewards_rebate' || target === 'rebate' || REBATE_KEY.test(key)) return '⚡'
  if (target === 'rewards_vip' || target === 'vip' || VIP_KEY.test(key)) return '👑'
  return undefined
}

/**
 * system 公告按 message_type 出图标，安全提醒固定为盾。
 * 活动按返水 / VIP 区分闪电和皇冠。
 * 其余消息仍按原来的模板键和分类图标。
 */
function noticeIcon(item: NoticeSource): string {
  if (item.category === 'system') {
    const messageType = (item.message_type || '').trim()
    const typedIcon = MESSAGE_TYPE_ICON[messageType]
    if (typedIcon) return typedIcon
  }

  const key = (item.template_key || '').trim().toLowerCase()
  const target = (item.cta_target || '').trim().toLowerCase()
  if (item.category === 'promo') {
    const icon = promoIcon(target, key)
    if (icon) return icon
  }
  if (PHISH_KEY.test(key)) return '🛡️'
  if (target === 'me_security' || item.msg_id.startsWith('s_') || DEVICE_SECURITY_KEY.test(key)) {
    return '🔒'
  }
  if (GENERIC_SECURITY_KEY.test(key)) return '🛡️'
  return CATEGORY_ICON[item.category] ?? '📢'
}

function mapCore(item: NoticeSource) {
  return {
    id: item.msg_id,
    cat: item.category,
    icon: noticeIcon(item),
    title: item.title,
    body: item.body,
    createdAt: item.created_at,
  }
}

export function mapNotificationItem(item: NotificationItemDto): NoticeItem {
  return { ...mapCore(item), unread: !item.is_read }
}

function text(value?: string | null) {
  return (value ?? '').trim()
}

/** 正文、明细、按钮文案、页脚都用接口已渲染的值；缺席字段按空处理 */
export function mapNotificationDetail(item: NotificationDetailDto): NoticeDetail {
  const page = mapPage(item)
  const ctaLabel = page ? text(item.cta_label) : ''
  return {
    ...mapCore(item),
    rows: (item.rows ?? [])
      .map(row => ({ label: text(row?.label), value: text(row?.value) }))
      .filter(row => row.label || row.value),
    footnote: text(item.footnote),
    ctaLabel,
    page: ctaLabel ? page : undefined,
  }
}
